import type {ReactElement} from 'react';
import React from 'react';
import type {NotificationsPanelHeaderProps} from './notifications-panel-header.types';
import styles from './notifications-panel-header.module.scss';
import {
  useAllNotifications,
  useMarkAllNotificationsAsRead,
  useNotificationPanelState,
  useNotificationSettingsPanelData,
} from '@Panels/notifications-panel/notifications-panel-hooks';
import type {PanelHeaderProps} from '@Components/panel';
import {PanelHeader} from '@Components/panel';
import {NotificationsPanelDisplayType} from '@Panels/notifications-panel/notifications-panel.types';
import {Text, TextSize} from '@Components/text';
import {setPanelDisplayType} from '@Panels/notifications-panel/notifications-panel-slice';
import {IconSize} from '@Components/icon-v2/icon.types';
import {useAppDispatch} from '@/hooks';
import {Button, Size, Type} from '@Components/button';
import {areAllNotificationsRead, areAllNotificationsTurnedOff} from '@Libraries/notifications-panel-library';
import {GA4EventName} from '@Libraries/ga-events';

export function NotificationsPanelHeader({...props}: NotificationsPanelHeaderProps): ReactElement {
  const panelState = useNotificationPanelState();
  const dispatch = useAppDispatch();
  const allFilteredNotifications = useAllNotifications();
  const notificationSettings = useNotificationSettingsPanelData();
  const markAllNotificationsAsRead = useMarkAllNotificationsAsRead();
  const isMobile = props.isMobile;
  const customClose = props.customClose;

  const onMarkAllAsReadClick = (allRead: boolean): void => {
    if (allRead) return;
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.NOTIFICATION_MARK_READ);
    markAllNotificationsAsRead(allFilteredNotifications);
  };
  const onSettingsBackButtonClick = (): void => {
    if (allFilteredNotifications.length === 0) {
      dispatch(setPanelDisplayType(NotificationsPanelDisplayType.EMPTY));
    } else if (areAllNotificationsTurnedOff(notificationSettings)) {
      dispatch(setPanelDisplayType(NotificationsPanelDisplayType.TURN_ON_NOTIFICATIONS));
    } else {
      dispatch(setPanelDisplayType(NotificationsPanelDisplayType.LOADED));
    }
  };

  const getNotificationsMarkAsReadLink = (): ReactElement => {
    const allRead = areAllNotificationsRead(allFilteredNotifications);
    return (
      <>
        <Text
          className={`${allRead ? `content-disabled ${styles.cursorDisabled}` : 'spacing-m-l-2 content-link _cursor-pointer'} spacing-m-r-2`}
          val={window.i18next.t('pmwjs_notifications_mark_all_read')}
          size={TextSize.XXSMALL}
          onClick={() => {
            onMarkAllAsReadClick(allRead);
          }}
        />

        <div className={`content-body bg-neutral-11 radius-round spacing-m-l-2 spacing-m-r-2 ${styles.lineDivider}`}></div>
      </>
    );
  };

  const getNotificationsSettingsButtonPanelHeader = (showMarkAllAsRead: boolean): ReactElement => {
    return (
      <div className={` flex-row-justify-between spacing-p-l-4 spacing-p-r-2`}>
        <div className="flex-center">
          <Text size={TextSize.XSMALL} bold className="content-body" val={window.i18next.t('pmwjs_notifications')} />
        </div>
        <div className="flexbox flex-center">
          {showMarkAllAsRead ? getNotificationsMarkAsReadLink() : null}
          <Button
            customClasses={`radius-8 spacing-p-2`}
            icon="icon-settings"
            type={Type.GHOST}
            iconClassName={'content-body'}
            size={Size.SMALL}
            onClick={() => {
              window.PMW.gtm.trackGA4CustomEvent(GA4EventName.NOTIFICATION_SETTINGS_CLICK);
              dispatch(setPanelDisplayType(NotificationsPanelDisplayType.SETTINGS));
            }}
          />
        </div>
      </div>
    );
  };

  const getPanelHeaderProps = (): PanelHeaderProps => {
    switch (panelState) {
      case NotificationsPanelDisplayType.EMPTY:
      case NotificationsPanelDisplayType.LOADING:
        return {
          title: window.i18next.t('pmwjs_notifications'),
          showCloseButton: isMobile,
        };

      case NotificationsPanelDisplayType.TURN_ON_NOTIFICATIONS:
        return {
          className: styles.stickyTopBar,
          title: isMobile ? window.i18next.t('pmwjs_notifications') : undefined,
          isCustomPanel: true,
          customPanel: !isMobile ? getNotificationsSettingsButtonPanelHeader(false) : undefined,
          showCloseButton: isMobile,
        };

      case NotificationsPanelDisplayType.LOADED:
        return {
          showCloseButton: isMobile,
          title: isMobile ? window.i18next.t('pmwjs_notifications') : undefined,
          className: `${styles.stickyTopBar} ${props.hasShadow ? `shadow-floating-elevation-2 ${styles.scrollColor}` : ''}`,
          isCustomPanel: !isMobile,
          customPanel: !isMobile ? getNotificationsSettingsButtonPanelHeader(true) : undefined,
        };

      case NotificationsPanelDisplayType.SETTINGS:
        return {
          onBackButton: (): void => {
            onSettingsBackButtonClick();
          },
          backButtonIconSize: isMobile ? IconSize.SIZE_ICON_20 : IconSize.SIZE_ICON_16,
          title: window.i18next.t('pmwjs_notifications_settings'),
          showBackButton: true,
          showCloseButton: true,
          className: isMobile ? '_borderless' : '',
        };

      default:
        return {
          title: window.i18next.t('pmwjs_notifications'),
          showCloseButton: isMobile,
        };
    }
  };

  const getNotificationsPanelHeader = (data: PanelHeaderProps): ReactElement<PanelHeaderProps> => {
    return (
      <PanelHeader
        isTitleBold
        isOpenedInModal={isMobile}
        onClose={customClose}
        centerAlignTitle={isMobile}
        titleTextSize={TextSize.XSMALL}
        className={data.className}
        title={data.title}
        isCustomPanel={data.isCustomPanel}
        customPanel={data.customPanel}
        showCloseButton={data.showCloseButton}
        showBackButton={data.showBackButton}
        backButtonIconSize={data.backButtonIconSize}
        closeButtonIconSize={IconSize.SIZE_ICON_20}
        onBackButton={data.onBackButton}
      />
    );
  };

  return getNotificationsPanelHeader(getPanelHeaderProps());
}
